/**
 * Printed table QR codes.
 *
 * Each sticker encodes a signed `YMT1` token, verified server-side by
 * `resolve_scan`. Because tokens are minted on read rather than stored, rotating
 * the signing secret invalidates every printed sticker at once, and disabling a
 * table kills just that one.
 *
 * The print view exists because the realistic workflow is "print this, laminate
 * it, put it on table 4" — not "email yourself a PNG".
 */

import { useCallback, useState } from 'react';
import { tableUrl, type RestaurantTable } from '@yummy/shared';
import { deleteTable, fetchTables, setTableActive } from '@yummy/db';
import {
  Button,
  Callout,
  Card,
  CardGrid,
  ConfirmDialog,
  copyText,
  EmptyState,
  ErrorBanner,
  Icon,
  LoadingState,
  PageHeading,
  Modal,
  Row,
  Screen,
  Stack,
  Text,
  ToneBadge,
  useAction,
  useAsync,
  useLocale,
  useResponsive,
  useToast,
} from '@yummy/ui-rnr';

import { useNavigate } from 'react-router-dom';

import { useMerchant } from '../app/auth';
import { TableQrCode } from '../components/TableQrCode';
import { customerOrigin } from '../lib/customer-url';
import { supabase } from '../lib/supabase';
import { LayoutGrid, Link, QrCode as QrCodeIcon } from 'lucide-react-native';


/** Side of the printed sticker, in points. */
const SHEET_SIZE = 500;

/**
 * Lists a restaurant's tables, with a printable QR sticker for each.
 *
 * @returns The tables screen.
 */
export function TablesScreen() {
  const { t } = useLocale();
  const toast = useToast();
  const navigate = useNavigate();
  const { restaurant } = useMerchant();
  const restaurantId = restaurant?.id ?? null;

  const tables = useAsync(
    async () => (restaurantId ? fetchTables(supabase, restaurantId) : []),
    [restaurantId],
  );

  const action = useAction();
  const [removing, setRemoving] = useState<{ id: string; label: string } | null>(null);
  const [printing, setPrinting] = useState<string | null>(null);

  const origin = customerOrigin();

  const confirmRemove = useCallback(async () => {
    if (!removing) return;
    const result = await action.run(() => deleteTable(supabase, removing.id));
    if (result !== null || !action.error) {
      toast.show(t('merchant.tables.deleted', { label: removing.label }), { tone: 'neutral' });
      setRemoving(null);
      tables.reload();
    }
  }, [removing, action, toast, tables, t]);

  const toggle = useCallback(
    async (id: string, label: string, active: boolean) => {
      const result = await action.run(() => setTableActive(supabase, id, active));
      if (result !== null || !action.error) {
        toast.show(
          active
            ? t('merchant.tables.enabled', { label })
            : t('merchant.tables.disabledToast', { label }),
          { tone: 'neutral' },
        );
        tables.reload();
      }
    },
    [action, toast, tables, t],
  );

  // ---------------------------------------------------------------------------
  const copyTableUrl = useCallback(
    async (table: RestaurantTable) => {
      const copied = await copyText(tableUrl(origin, table.token));
      toast.show(t(copied ? 'common.copied' : 'merchant.tables.copyFailed'), {
        tone: copied ? 'success' : 'danger',
      });
    },
    [origin, toast, t],
  );

  // ---------------------------------------------------------------------------

  if (!restaurantId) {
    return (
      <Screen>
        <EmptyState icon={QrCodeIcon} title={t('merchant.tables.noRestaurant')} />
      </Screen>
    );
  }

  if (tables.initial) {
    return (
      <Screen>
        <LoadingState message={t('merchant.tables.loading')} />
      </Screen>
    );
  }

  if (tables.error) {
    return (
      <Screen>
        <ErrorBanner message={tables.error} onRetry={tables.reload} />
      </Screen>
    );
  }

  const list = tables.data ?? [];
  const addLabel = t('merchant.tables.add');

  return (
    <Screen constrain={false}>
      <Stack gap="xl">
        {/* 240 keeps a readable floor and lets the row push the button onto its
            own line first — this screen's action is one button, so the floor can
            afford to be the default. */}
        <PageHeading
          title={t('merchant.tables.title')}
          subtitle={t('merchant.tables.intro')}
          action={
            <Button onPress={() => navigate('/tables/new')} accessibilityLabel={addLabel}>
              <Text>{addLabel}</Text>
            </Button>
          }
        />

        {action.error ? <ErrorBanner message={action.error} onRetry={action.clearError} /> : null}

        <Callout
          tone="info"
          title={t('merchant.tables.neverExpire')}
          message={t('merchant.tables.neverExpireHint')}
        />

        {list.length === 0 ? (
          <EmptyState
            icon={LayoutGrid}
            title={t('merchant.tables.empty')}
            message={t('merchant.tables.emptyHint')}
            actionLabel={addLabel}
            onAction={() => navigate('/tables/new')}
          />
        ) : (
          /* Bounded on both sides: the card is sized around its QR sticker, and
             a short last row must not stretch one card across the board. */
          <CardGrid minWidth={240} maxWidth={380}>
            {list.map((table) => (
              <Card key={table.id} className="p-4">
                <Stack gap="md" align="center">
                  <Row spread gap="md" className="w-full">
                    <Stack gap="xs">
                      <Text className="text-base font-semibold">{table.label}</Text>
                      {table.seats ? (
                        <Text className="text-subtle-foreground text-xs">
                          {t('merchant.tables.seats', { count: table.seats })}
                        </Text>
                      ) : null}
                    </Stack>
                    {table.is_active ? (
                      <ToneBadge label={t('merchant.tables.active')} tone="success" size="sm" />
                    ) : (
                      <ToneBadge label={t('merchant.tables.disabled')} tone="danger" size="sm" />
                    )}
                  </Row>

                  <TableQrCode
                    value={tableUrl(origin, table.token)}
                    size={176}
                    label={table.label}
                    // The card is a preview, not the artifact, so it stays at the
                    // lighter level: `Q` pays for the mark in the middle without
                    // spending modules the way `H` would at this size.
                    level="Q"
                  />

                  <Row gap="sm" justify="center" wrap>
                    <Button
                      size="sm"
                      variant="secondary"
                      onPress={() => setPrinting(table.id)}
                      accessibilityLabel={t('common.print')}>
                      <Text>{t('common.print')}</Text>
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onPress={() => void copyTableUrl(table)}
                      accessibilityLabel={t('merchant.tables.copyUrl')}>
                      <Icon as={Link} size={14} aria-hidden className="text-muted-foreground" />
                      <Text>{t('merchant.tables.copyUrl')}</Text>
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onPress={() => void toggle(table.id, table.label, !table.is_active)}
                      accessibilityLabel={
                        table.is_active ? t('merchant.tables.disable') : t('merchant.tables.enable')
                      }>
                      <Text>
                        {table.is_active ? t('merchant.tables.disable') : t('merchant.tables.enable')}
                      </Text>
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onPress={() => setRemoving({ id: table.id, label: table.label })}
                      accessibilityLabel={t('common.delete')}>
                      <Text>{t('common.delete')}</Text>
                    </Button>
                  </Row>
                </Stack>
              </Card>
            ))}
          </CardGrid>
        )}
      </Stack>

      {/* ------------------------------------------------------------------ */}
      {/* Print                                                              */}
      {/* ------------------------------------------------------------------ */}

      <PrintSheet
        table={list.find((t) => t.id === printing) ?? null}
        origin={origin}
        onClose={() => setPrinting(null)}
      />

      <ConfirmDialog
        visible={removing !== null}
        title={removing ? t('merchant.tables.deleteTitle', { label: removing.label }) : ''}
        message={t('merchant.tables.deleteMessage')}
        confirmLabel={t('common.delete')}
        destructive
        busy={action.pending}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />
    </Screen>
  );
}

/**
 * A large, print-oriented view of one sticker.
 *
 * Sized so a browser print of this dialog fills roughly A5. The sticker carries
 * the table and the code and nothing else: the restaurant's name is already on
 * the wall around it, and a printed URL is noise next to a code that scans.
 *
 * @param props The table, origin, and close handler.
 * @returns The print sheet, or nothing when no table is being printed.
 */
function PrintSheet({
  table,
  origin,
  onClose,
}: {
  table: { id: string; label: string; token: string } | null;
  origin: string;
  onClose: () => void;
}) {
  const { t } = useLocale();
  const { width, height } = useResponsive();

  if (!table) return null;

  // A sticker is square, and the code has to fit inside it. The side is bounded
  // by the viewport so the same sheet prints from a phone as from the iPad it
  // was designed for, and the code takes what is left after the label and the
  // buttons.
  const side = Math.max(260, Math.min(SHEET_SIZE, width - 48, height - 140));
  const codeSize = Math.max(120, side - 200);

  return (
    <Modal
      visible
      onClose={onClose}
      square
      maxWidth={side}
      footer={
        <Row gap="sm" justify="end">
          <Button
            variant="secondary"
            onPress={onClose}
            accessibilityLabel={t('common.close')}>
            <Text>{t('common.close')}</Text>
          </Button>
          <Button
            onPress={() => window.print()}
            accessibilityLabel={t('merchant.tables.printThis')}>
            <Text>{t('merchant.tables.printThis')}</Text>
          </Button>
        </Row>
      }
    >
      {/* The sticker itself is the square. The modal only guarantees the room. */}
      <Stack gap="lg" align="center" className="aspect-square w-full justify-center">
        <Stack gap="xs" align="center">
          <Text className="text-3xl font-bold">{table.label}</Text>
          <Text className="text-muted-foreground text-center text-base">
            {t('merchant.tables.printSubtitle')}
          </Text>
        </Stack>

        <TableQrCode
          value={tableUrl(origin, table.token)}
          size={codeSize}
          // The sticker is the one that has to keep working: laminated, wiped,
          // and left in the sun for a year. `H` is the whole point of having
          // error correction to spend, and this is the largest the code is ever
          // rendered.
          level="H"
          label={table.label}
        />
      </Stack>
    </Modal>
  );
}
